import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IGetDishInfoV2, IItemForGetShortDishInfo } from 'types';
import { suggestedModalInitialState, SuggestedModalContext } from './suggestedModalContext';
import { suggestedModalReducer } from './suggestedModalReducer';
import { SuggestedModalActionTypes } from './suggestedModalActions';
import { useFetchData } from 'hooks/useFetchData';
import { getDishInfoV2 } from 'backend/dishes';

export const SuggestedModalState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(suggestedModalReducer, suggestedModalInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const dishID = params.get('dishID');

	const { response, loading } = useFetchData<IGetDishInfoV2, string>(dishID ? getDishInfoV2 : null, dishID || '');

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: SuggestedModalActionTypes.SET_ALL_DATA,
				payload: {
					dishInfo: response.data,
					IDsSelectedRestaurant: response.data.restaurants.map((restaurant) => restaurant.id),
					fetchedSuggestedDishes: response.data.suggestedDishes.map((suggestedDish) => ({
						...suggestedDish,
						externalId: null,
					})),
					selectedSuggestedDishes: response.data.suggestedDishes.map((suggestedDish) => suggestedDish.id),
				},
			});
		}
	}, [response]);

	const setSuggestedDishCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: SuggestedModalActionTypes.SET_SUGGESTED_DISH_CATEGORY,
			payload: {
				selectedSuggestedDishCategory: value,
			},
		});
	}, []);

	const setSuggestedDishSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: SuggestedModalActionTypes.SET_SUGGESTED_DISH_SUB_CATEGORY,
			payload: {
				selectedSuggestedDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedSuggestedDishes = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedSuggestedDishes.includes(el.id));
			const filteredFetchedSuggestedDishes = state.fetchedSuggestedDishes.filter((el) =>
				state.selectedSuggestedDishes.includes(el.id),
			);

			dispatch({
				type: SuggestedModalActionTypes.SET_FETCHED_SUGGESTED_DISHES,
				payload: {
					fetchedSuggestedDishes: [...filteredValue, ...filteredFetchedSuggestedDishes],
				},
			});
		},
		[state.selectedSuggestedDishes, state.fetchedSuggestedDishes],
	);

	const setSuggestedDishes = useCallback((value: string[]) => {
		dispatch({
			type: SuggestedModalActionTypes.SET_SUGGESTED_DISHES,
			payload: {
				selectedSuggestedDishes: value,
			},
		});
	}, []);

	const removeSuggestedDish = useCallback(
		(value: string) => {
			const updatedSelectedSuggestedDishes = state.selectedSuggestedDishes.filter((el) => el !== value);

			dispatch({
				type: SuggestedModalActionTypes.SET_SUGGESTED_DISHES,
				payload: {
					selectedSuggestedDishes: updatedSelectedSuggestedDishes,
				},
			});
		},
		[state.selectedSuggestedDishes],
	);

	return (
		<SuggestedModalContext.Provider
			value={{
				setSuggestedDishCategory,
				setSuggestedDishSubCategory,
				setFetchedSuggestedDishes,
				setSuggestedDishes,
				removeSuggestedDish,
				dishInfo: state.dishInfo,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				selectedSuggestedDishCategory: state.selectedSuggestedDishCategory,
				selectedSuggestedDishSubCategory: state.selectedSuggestedDishSubCategory,
				fetchedSuggestedDishes: state.fetchedSuggestedDishes,
				selectedSuggestedDishes: state.selectedSuggestedDishes,
			}}>
			{children}
		</SuggestedModalContext.Provider>
	);
};
